import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { TrailView } from "./ScrRegister";
import { CheckCircle2, XCircle, Inbox } from "lucide-react";

const inr = (v) => (v ? `₹${Number(v).toLocaleString("en-IN")}` : "—");

export default function ScrInbox() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState({});
  const [busy, setBusy] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/scr/inbox").then(({ data }) => setRows(data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const act = async (r, action) => {
    setBusy(r.scr_id);
    try {
      const { data } = await api.post(`/scr/${r.scr_id}/${action}`, { comments: comments[r.scr_id] || "" });
      toast.success(action === "approve"
        ? (data.final ? `${r.scr_no} fully approved — new sanction version written` : `${r.scr_no} approved and moved to next stage`)
        : `${r.scr_no} rejected`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="scr-inbox-page">
      <div className="mb-6">
        <h1 className="font-serif text-2xl sm:text-3xl text-[#0E2240] tracking-tight">Approval Inbox</h1>
        <p className="text-sm text-[#6B7280] mt-1">Sanction change requests awaiting your decision</p>
      </div>

      {loading ? (
        <div className="text-sm text-[#6B7280] py-10 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-sm p-10 text-center">
          <Inbox size={28} className="mx-auto text-[#BF9B30]" strokeWidth={1.5} />
          <div className="text-sm text-[#6B7280] mt-3">Nothing pending at your stage. All clear.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {rows.map((r) => (
            <div key={r.scr_id} data-testid={`inbox-card-${r.scr_id}`} className="bg-white border border-gray-200 rounded-sm p-5 sm:p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold text-[#0E2240]">{r.scr_no}</div>
                  <div className="font-serif text-lg text-[#0E2240] mt-1">{r.desig_name}</div>
                  <div className="text-xs text-[#6B7280]">{r.branch_name} · {r.dept_name}</div>
                </div>
                <div className="text-right tabular-nums">
                  <div className="text-2xl font-semibold text-[#0E2240]">{r.current_count} → {r.proposed_count}</div>
                  <div className={`text-xs font-medium ${r.delta > 0 ? "text-[#1B7F3B]" : "text-[#BB162B]"}`}>{r.delta > 0 ? "+" : ""}{r.delta} positions</div>
                </div>
              </div>
              <div className="mt-3 text-sm text-[#0E2240] leading-snug">{r.justification}</div>
              <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-[#6B7280] tabular-nums">
                <span>Driver: {r.business_driver || "—"}</span>
                <span>Cost impact: {inr(r.monthly_cost_impact)}/mo</span>
                <span>By {r.requested_by_name} · {new Date(r.requested_on).toLocaleDateString("en-IN")}</span>
              </div>
              <div className="mt-4">
                <TrailView scr={r} />
              </div>
              <input
                data-testid={`inbox-comment-${r.scr_id}`}
                className="mt-4 w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#BF9B30]"
                placeholder="Comments (optional)"
                value={comments[r.scr_id] || ""}
                onChange={(e) => setComments((p) => ({ ...p, [r.scr_id]: e.target.value }))}
              />
              <div className="mt-3 flex gap-3">
                <button data-testid={`inbox-approve-${r.scr_id}`} disabled={busy === r.scr_id} onClick={() => act(r, "approve")}
                  className="flex-1 flex items-center justify-center gap-2 bg-[#1B7F3B] text-white text-sm py-2.5 rounded-sm hover:bg-[#156632] transition-colors duration-200 disabled:opacity-50">
                  <CheckCircle2 size={15} /> Approve
                </button>
                <button data-testid={`inbox-reject-${r.scr_id}`} disabled={busy === r.scr_id} onClick={() => act(r, "reject")}
                  className="flex-1 flex items-center justify-center gap-2 bg-[#BB162B] text-white text-sm py-2.5 rounded-sm hover:bg-[#8f1021] transition-colors duration-200 disabled:opacity-50">
                  <XCircle size={15} /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
